import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Hand, ArrowLeft, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { createMotorTest } from '@/utils/api';
import { toast } from 'sonner';

export default function TappingTest({ patient }) {
  const navigate = useNavigate();
  const [testState, setTestState] = useState('ready'); // ready, running, finished
  const [timeLeft, setTimeLeft] = useState(10);
  const [taps, setTaps] = useState([]);
  const [lastButton, setLastButton] = useState(null);
  const timerRef = useRef(null);

  const startTest = () => {
    setTestState('running');
    setTimeLeft(10);
    setTaps([]);
    setLastButton(null);

    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          setTestState('finished');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleTap = (button) => {
    if (testState !== 'running') return;

    const now = Date.now();
    setTaps((prev) => [...prev, { button, time: now }]);
    setLastButton(button);
  };

  const calculateScore = () => {
    if (taps.length === 0) return 0;

    // Punteggio basato su velocità e alternanza
    const alternatingTaps = taps.filter((tap, idx) => {
      if (idx === 0) return true;
      return tap.button !== taps[idx - 1].button;
    });

    const speed = alternatingTaps.length / 10; // taps per secondo
    const consistency = (alternatingTaps.length / taps.length) * 100;

    return Math.min(100, Math.round((speed * 10 + consistency) / 2));
  };

  const saveTest = async () => {
    const score = calculateScore();

    try {
      await createMotorTest({
        patient_id: patient.id,
        tipo: 'tapping',
        punteggio: score,
        durata: 10,
        dettagli: {
          total_taps: taps.length,
          alternating_taps: taps.filter((tap, idx) => idx === 0 || tap.button !== taps[idx - 1].button).length,
        },
      });

      toast.success('Test salvato con successo!');
      setTimeout(() => navigate('/test-motori'), 2000);
    } catch (error) {
      console.error('Errore nel salvataggio del test:', error);
      toast.error('Errore nel salvataggio del test');
    }
  };

  useEffect(() => {
    if (testState === 'finished' && taps.length > 0) {
      saveTest();
    }
  }, [testState]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <div className="p-4 flex items-center justify-between border-b border-slate-200 bg-white">
        <Button
          data-testid="btn-back"
          variant="ghost"
          onClick={() => navigate('/test-motori')}
          className="min-h-[48px] rounded-2xl"
        >
          <ArrowLeft className="w-6 h-6 mr-2" />
          Indietro
        </Button>
        <h1 className="text-xl font-bold text-slate-900">Tapping Test</h1>
        <div className="w-24"></div>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center p-6">
        <AnimatePresence mode="wait">
          {testState === 'ready' && (
            <motion.div
              key="ready"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="text-center max-w-md"
            >
              <div className="w-24 h-24 rounded-3xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center mx-auto mb-6 soft-shadow-lg">
                <Hand className="w-12 h-12 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-slate-900 mb-4">Pronto per il Test?</h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                Alterna rapidamente tra i due pulsanti per 10 secondi. Cerca di mantenere un ritmo costante.
              </p>
              <Button
                data-testid="btn-start-test"
                onClick={startTest}
                className="w-full min-h-[64px] text-lg font-semibold rounded-2xl bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-lg hover:shadow-xl transition-all duration-300 active:scale-[0.98]"
              >
                Inizia il Test
              </Button>
            </motion.div>
          )}

          {testState === 'running' && (
            <motion.div
              key="running"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="w-full max-w-2xl"
            >
              <div className="text-center mb-8">
                <motion.div
                  key={timeLeft}
                  initial={{ scale: 1.2 }}
                  animate={{ scale: 1 }}
                  className="text-8xl font-bold text-blue-600 mb-2"
                >
                  {timeLeft}
                </motion.div>
                <p className="text-xl text-slate-600">Tocca: {taps.length}</p>
              </div>

              <div className="grid grid-cols-2 gap-4 h-[400px]">
                <motion.button
                  data-testid="tap-button-left"
                  whileTap={{ scale: 0.95 }}
                  onClick={() => handleTap('left')}
                  className={`w-full h-full rounded-3xl font-bold text-2xl transition-all duration-200 ${
                    lastButton === 'left'
                      ? 'bg-blue-600 text-white shadow-xl scale-[0.98]'
                      : 'bg-white text-blue-600 border-4 border-blue-600 shadow-lg'
                  }`}
                >
                  SINISTRA
                </motion.button>

                <motion.button
                  data-testid="tap-button-right"
                  whileTap={{ scale: 0.95 }}
                  onClick={() => handleTap('right')}
                  className={`w-full h-full rounded-3xl font-bold text-2xl transition-all duration-200 ${
                    lastButton === 'right'
                      ? 'bg-green-600 text-white shadow-xl scale-[0.98]'
                      : 'bg-white text-green-600 border-4 border-green-600 shadow-lg'
                  }`}
                >
                  DESTRA
                </motion.button>
              </div>
            </motion.div>
          )}

          {testState === 'finished' && (
            <motion.div
              key="finished"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center max-w-md"
            >
              <div className="w-24 h-24 rounded-3xl bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center mx-auto mb-6 soft-shadow-lg">
                <TrendingUp className="w-12 h-12 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-slate-900 mb-2">Test Completato!</h2>
              <div className="text-7xl font-bold text-blue-600 my-6">{calculateScore()}</div>
              <p className="text-xl text-slate-600 mb-2">Punteggio</p>
              <p className="text-base text-slate-500 mb-8">
                Tocchi totali: {taps.length} | Alternati: {taps.filter((tap, idx) => idx === 0 || tap.button !== taps[idx - 1].button).length}
              </p>
              <p className="text-sm text-slate-500">Salvataggio in corso...</p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
